import { ArrowDownLeft, ArrowUpRight, CalendarDays, HandCoins, PiggyBank, Printer, Receipt } from "lucide-react";
import { format } from "date-fns";
import { id as idLocale } from "date-fns/locale";
import { useState } from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { dateStr, fmtDateTime, rupiah, TX_LABELS, txTone } from "@/lib/format";
import { cn } from "@/lib/utils";
import { ReceiptDialog, useReceipt } from "@/components/Receipt";

const ICONS = { setor: ArrowDownLeft, tarik: ArrowUpRight, pencairan: HandCoins, angsuran: Receipt, potongan: PiggyBank };
const TONES = { setor: "text-orange-600", angsuran: "text-green-600", tarik: "text-orange-700", pencairan: "text-red-600", potongan: "text-orange-500" };
const PRINTABLE = ["setor", "tarik", "angsuran", "potongan"];

export const TxTypeBadge = ({ type }) => {
  const Icon = ICONS[type] || Receipt;
  return (
    <span className={cn("inline-flex items-center gap-1.5 text-xs font-semibold", TONES[type])}>
      <Icon className="h-4 w-4" />{TX_LABELS[type] || type}
    </span>
  );
};

const toDate = (s) => (s ? new Date(`${s}T00:00:00`) : undefined);
const localStr = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export const DatePicker = ({ value, onChange, min, max, testId }) => {
  const [open, setOpen] = useState(false);
  const sel = toDate(value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className="h-11 w-[150px] rounded-md border border-input bg-background px-3 flex items-center gap-2 text-sm font-mono hover:bg-muted transition-colors" data-testid={testId}>
          <CalendarDays className="h-4 w-4 text-muted-foreground" />{sel ? format(sel, "dd/MM/yyyy") : "Pilih tanggal"}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start" data-testid={`${testId}-calendar`}>
        <Calendar mode="single" locale={idLocale} selected={sel} defaultMonth={sel} weekStartsOn={1}
          disabled={[...(min ? [{ before: toDate(min) }] : []), ...(max ? [{ after: toDate(max) }] : []), { after: toDate(dateStr(new Date())) }]}
          onSelect={(d) => { if (d) { onChange(localStr(d)); setOpen(false); } }} initialFocus />
      </PopoverContent>
    </Popover>
  );
};

export const DateRange = ({ start, end, onChange, testId = "date-range" }) => (
  <div className="flex items-center gap-2" data-testid={testId}>
    <DatePicker value={start} max={end} onChange={(v) => onChange(v, end)} testId={`${testId}-start`} />
    <span className="text-muted-foreground text-sm">s/d</span>
    <DatePicker value={end} min={start} onChange={(v) => onChange(start, v)} testId={`${testId}-end`} />
  </div>
);

const PrintBtn = ({ t, onOpen }) => (PRINTABLE.includes(t.type) ? (
  <button type="button" onClick={() => onOpen(t)} className="h-9 w-9 grid place-items-center rounded-full hover:bg-muted text-muted-foreground hover:text-foreground transition-colors" aria-label="Struk" data-testid={`tx-receipt-btn-${t.id}`}>
    <Printer className="h-4 w-4" />
  </button>
) : <span className="inline-block w-9" />);

export const TxTable = ({ rows, showMember = true, testId = "tx-table" }) => {
  const receipt = useReceipt();
  if (!rows.length) return <p className="text-sm text-muted-foreground py-10 text-center" data-testid={`${testId}-empty`}>Belum ada transaksi pada rentang ini.</p>;
  return (
    <>
      <div className="hidden md:block overflow-x-auto" data-testid={testId}>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Waktu</TableHead>
              {showMember && <TableHead>Anggota</TableHead>}
              <TableHead>Jenis</TableHead>
              <TableHead>Resort</TableHead>
              <TableHead>Petugas</TableHead>
              <TableHead>Kanal</TableHead>
              <TableHead className="text-right">Nominal</TableHead>
              <TableHead className="w-12" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((t) => (
              <TableRow key={t.id} className={txTone(t.type)} data-testid={`tx-row-${t.id}`}>
                <TableCell className="whitespace-nowrap text-sm">{fmtDateTime(t.created_at)}</TableCell>
                {showMember && <TableCell><p className="font-medium">{t.member_name}</p><p className="font-mono text-xs text-muted-foreground">{t.nik}</p></TableCell>}
                <TableCell><TxTypeBadge type={t.type} /></TableCell>
                <TableCell className="text-sm">{t.resort_name || "-"}</TableCell>
                <TableCell className="text-sm">{t.by_name}</TableCell>
                <TableCell className="text-xs capitalize text-muted-foreground">{t.channel}</TableCell>
                <TableCell className="text-right font-mono font-semibold">{rupiah(t.amount)}</TableCell>
                <TableCell className="pr-2"><PrintBtn t={t} onOpen={receipt.open} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <div className="md:hidden divide-y" data-testid={`${testId}-mobile`}>
        {rows.map((t) => (
          <div key={t.id} className={cn("py-3 px-3 flex items-start justify-between gap-3 rounded-lg my-1", txTone(t.type))}>
            <div className="min-w-0">
              <TxTypeBadge type={t.type} />
              {showMember && <p className="font-medium truncate mt-1">{t.member_name}</p>}
              <p className="text-xs text-muted-foreground">{fmtDateTime(t.created_at)} · {t.by_name}{t.resort_name ? ` · ${t.resort_name}` : ""}</p>
            </div>
            <div className="flex items-center gap-1">
              <p className="font-mono font-semibold text-sm whitespace-nowrap">{rupiah(t.amount)}</p>
              <PrintBtn t={t} onOpen={receipt.open} />
            </div>
          </div>
        ))}
      </div>
      <ReceiptDialog tx={receipt.tx} onClose={receipt.close} />
    </>
  );
};
